/**
 * A custom hook for managing submenus in the application navigation.
 * Provides submenu configurations for different sections and determines
 * which submenu items to display based on the current route.
 */
import { useAtom } from "jotai";
import { useEffect, useState, useMemo } from "react";
import { routeAndPathAtom } from "../atoms";
import { Platform } from "react-native";

/**
 * Interface for a menu item in a submenu.
 * @interface MenuProps
 * @property {string} title - Display text for the menu item
 * @property {string} route - Navigation route name for the menu item
 * @property {Object} params - Optional navigation parameters
 */
export interface MenuProps {
  title: string;
  route: string;
  params: Object;
}

/**
 * Hook to determine which submenu items to display based on the current route.
 * Returns the appropriate menu items for the active section.
 *
 * @returns {MenuProps[]|null} Menu items to display in the submenu, or null if no submenu should be shown
 */
export const useShowSubmenu = () => {
  const [menuToShow, setMenuToShow] = useState(null);
  const [routeAndPath] = useAtom(routeAndPathAtom);

  /**
   * Configuration for the Watch section submenu.
   * Defines which routes should show the Watch submenu and the menu items to display.
   */
  const watchSubmenu = useMemo(
    () => ({
      routes: [
        "Watch",
        "Replays",
        "OnDemand",
        "CatchUp",
        "TvSchedule",
        "RtvPlaySchedule",
        "IndividualReplay",
        "IndividualOnDemand",
        "CombinedOnDemand",
      ],
      menu: [
        { title: "Live", route: "Watch" },
        { title: "Replays", route: "Replays" },
        { title: "On Demand", route: "OnDemand" },
        { title: "Catch Up", route: "CatchUp" },
        { title: "Tv Schedule", route: "TvSchedule" },
        { title: "RTV Play Schedule", route: "RtvPlaySchedule" },
      ],
    }),
    []
  );

  /**
   * Configuration for the Tips section submenu.
   * Defines which routes should show the Tips submenu and the menu items to display.
   */
  const tipsSubmenu = useMemo(
    () => ({
      routes: [
        "RTVTipsters",
        //  "AllTips",
        "NapOfTheDay",
        "IndividualTipster",
      ],
      menu: [
        { title: "Racing TV Tipsters", route: "RTVTipsters" },
        // { title: "All Tips", route: "AllTips" },
        { title: "NAP of the Day", route: "NapOfTheDay" },
      ],
    }),
    []
  );

  /**
   * Configuration for the News section submenu.
   * Defines which routes should show the News submenu and the menu items to display.
   */
  const newsSubmenu = useMemo(
    () => ({
      routes: [
        "AllNews",
        "Latest",
        "Highlights",
        "Columnists",
        "MostViewed",
        "IndividualColumnist",
        "NewsPost",
      ],
      menu: [
        { title: "All", route: "AllNews" },
        { title: "Latest", route: "Latest" },
        { title: "Highlights", route: "Highlights" },
        { title: "Columnists", route: "Columnists" },
        { title: "Most Viewed", route: "MostViewed" },
      ],
    }),
    []
  );

  /**
   * Configuration for the Races section submenu.
   * Defines which routes should show the Races submenu and the menu items to display.
   */
  const racesSubmenu = useMemo(
    () => ({
      routes: [
        "Racecards",
        "TodaysRunners",
        "TomorrowsRunners",
        "NonRunners",
        "IndividualRacecard",
        "HorseRacingCalendar",
      ],
      menu: [
        { title: "Racecards", route: "Racecards" },
        { title: "Today's Runners", route: "TodaysRunners" },
        { title: "Non-Runners", route: "NonRunners" },
        {
          title: "Tomorrow's Runners",
          route: "TomorrowsRunners",
        },
        {
          title: "Racing Calendar",
          route: "HorseRacingCalendar",
        },
      ],
    }),
    []
  );

  /**
   * Configuration for the Results section submenu.
   * Defines which routes should show the Results submenu and the menu items to display.
   */
  const resultsSubmenu = useMemo(
    () => ({
      routes: ["Results", "FastResults", "IndividualResult"],
      menu: [
        { title: "Results", route: "Results" },
        { title: "Fast Results", route: "FastResults" },
      ],
    }),
    []
  );

  /**
   * Configuration for the Members section submenu.
   * Defines which routes should show the Members submenu and the menu items to display.
   * Handles platform-specific differences for the Syndicate menu item.
   */
  const membersSubMenu = useMemo(
    () => ({
      routes: [
        "Benefits",
        "RtvExtra",
        "ClubDays",
        "Magazine",
        "Rewards4Racing",
        "BenefitsPage",
        "syndicate",
      ],
      menu: [
        { title: "Benefits", route: "Benefits" },
        { title: "Racing TV extra", route: "RtvExtra" },
        { title: "Club Days", route: "ClubDays" },
        Platform.OS === "web"
          ? { title: "Syndicate", route: "syndicate" }
          : {
              title: "Syndicate",
              route: "CmsPage",
              params: { cmsPage: "syndicate" },
            },

        { title: "Magazine", route: "Magazine" },
        { title: "Rewards4Racing", route: "Rewards4Racing" },
      ],
    }),
    []
  );

  /**
   * Determines which submenu to show based on the current route.
   * Updates the menuToShow state when the route changes.
   */
  useEffect(() => {
    /**
     * Collection of all available submenus for iteration
     */
    const submenus = [
      tipsSubmenu,
      newsSubmenu,
      watchSubmenu,
      racesSubmenu,
      resultsSubmenu,
      membersSubMenu,
    ];

    /**
     * Determines which submenu to show based on the current route.
     * Returns the menu items for the matching submenu or null if no match.
     *
     * @returns {MenuProps[]|null} The appropriate menu items or null
     */
    const determineSubMenu = () => {
      for (let submenu of submenus) {
        if (submenu.routes.includes(routeAndPath.route)) {
          return submenu.menu;
        }
      }
      return null;
    };

    setMenuToShow(determineSubMenu());
  }, [routeAndPath.route]);

  return menuToShow;
};
